import HighchartsReact from "highcharts-react-official";
import Highcharts, { chart, Chart } from "highcharts";
import HC_exporting from "highcharts/modules/exporting";
import HC_stock from "highcharts/modules/stock";
import { useMutation, useQuery } from "@apollo/client";
import React, { useState, useEffect, useContext, useRef } from "react";
import _ from "lodash";

import createHighchartOptions from "../../utils/create-highchart-options";
import { prepareQueryForGraphs } from "../../queries/stockpiles.gql";
import CreateFlagModal from "../graph-flag-create-modal/graph-flag-create-modal.component";
import DeleteFlagModal from "../graph-flag-delete-modal/graph-flag-delete-modal.component";
import ModalWindow from "../HOC/modal-window/modal-window.component";
import { prepareQueryForBookmarks } from "../../queries/bookmarks.gql";
import {
  SEND_CREATE_BOOKMARK,
  SEND_DELETE_BOOKMARK,
} from "../../mutations/bookmarks.gql";
import { MatchContext, UserInfo } from "../../context";
import {
  APP_INSIGHTS_EVENTS,
  GRAPHS_BANDS_COLORS,
  GRAPHS_LINES_COLORS,
} from "../../constants";
import LoaderBar from "../common/loader-bar.component";
import IntraTableMessage from "../HOC/intratable-message/intratable-message.component";
import ErrorMessage from "../common/error-message.component";
import useTrackEvent from "../../utils/hooks/use-track-app-event";

HC_exporting(Highcharts);
HC_stock(Highcharts);

export interface ChartOptionsType {
  chart: object;
  exporting: object;
  plotOptions: any;
  series: any;
  title: object;
  tooltip: object;
  xAxis: object;
  yAxis: object;
}

const GraphHighchart = ({ tag, stockpiles, dataId }) => {
  const trackEvent = useTrackEvent();
  const { state } = useContext(MatchContext);
  const userInfo = useContext(UserInfo);
  const [savedBookmarks, setSavedBookmarks] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [chartOptions, setChartOptions] = useState<ChartOptionsType>();
  const [showCreateFlagModal, setShowCreateFlagModal] = useState(false);
  const [showDeleteFlagModal, setShowDeleteFlagModal] = useState(false);
  const [flagTitle, setFlagTitle] = useState("");
  const [flagComment, setFlagComment] = useState("");
  const [graphClickEvent, setGraphClickEvent] = useState({});
  const [bookmarkForDelete, setBookmarkForDelete] = useState("");
  const [plotBands, setPlotBands] = useState([]);

  function hasNumberPairs(data: (string | number)[][]): boolean {
    for (const pair of data) {
      if (
        !Array.isArray(pair) ||
        pair.length !== 2 ||
        typeof pair[0] !== "number" ||
        typeof pair[1] !== "number"
      ) {
        return false;
      }
    }
    return true;
  }

  const hasStockpileData = (chartOptions) => {
    const dataLength = chartOptions?.series
      .map((x) => x.data.length)
      .reduce((a, b) => a + b);
    return dataLength && dataLength !== 0;
  };

  const { data, loading, error } = useQuery(
    prepareQueryForGraphs(stockpiles, tag),
    { fetchPolicy: "no-cache" }
  );
  const { data: bookmarks, loading: bookmarksLoading } = useQuery(
    prepareQueryForBookmarks(stockpiles, tag),
    { fetchPolicy: "no-cache" }
  );

  const [createBookmark] = useMutation(SEND_CREATE_BOOKMARK, {
    onCompleted: (data) => {
      setSavedBookmarks((arr) => [...arr, data.createBookmark]);
    },
  });
  const [deleteBookmark] = useMutation(SEND_DELETE_BOOKMARK);

  const chartRef = useRef<{
    chart: Chart;
    container: React.RefObject<HTMLDivElement>;
  }>(null);

  const exportFileName = `${tag}-${stockpiles.join("-")}`;

  useEffect(() => {
    if (data && bookmarks) {
      let bands = [];
      const graphValues = Object.values(JSON.parse(JSON.stringify(data))).map(
        (item: any, index: number) => {
          if (item.lte) item.name = "LTE " + item.name;
          item.color = GRAPHS_LINES_COLORS[index];
          bands.push({
            from: item.plotBands.from,
            to: item.plotBands.to,
            color: GRAPHS_BANDS_COLORS[index],
            id: item.id,
            zIndex: 1,
            label: {
              text: item.id.replace(/\s/g, "<br/>"),
            },
          });
          return item;
        }
      );
      const graphBookmarks = Object.values(
        JSON.parse(JSON.stringify(bookmarks))
      )
        .filter((item: any) => item.length)
        .map((item: any) => {
          const spNum = item[0]?.stockpileId.match(/\d+/g)[0];

          return {
            type: "flags",
            shape: "squarepin",
            name: `🔖 ${spNum}`,
            id: `bookmarks_${spNum}`,
            _id: item[0].id,
            onSeries: item[0].stockpileId,
            width: 20,
            data: item,
          };
        });
      setGraphData([...graphValues, ...graphBookmarks]);
      setPlotBands(bands);
    }
  }, [data, bookmarks, stockpiles]);

  useEffect(() => {
    if (graphData?.length) {
      const options = createHighchartOptions({
        data: state.graphsBookmarks[dataId]
          ? graphData
          : graphData.filter((item) => item?.type !== "flags"),
        plotBands: state.graphsBestDays[dataId] ? plotBands : [],
        sensor: tag,
        name: exportFileName,
        pointClick: handlePointClick,
        trackEvent,
        dataId,
      });

      setChartOptions(options);
    }
  }, [graphData, state.graphsBestDays[dataId], state.graphsBookmarks[dataId]]);

  const handleSave = () => {
    const e: any = graphClickEvent;
    const x = e.options.x;
    const id = e.series.userOptions.id.toLowerCase();
    const spNum = id.match(/\d+/g)[0];
    const flag = {
      x,
      title: flagTitle,
      text: flagComment,
    };
    let newData = [...graphData];
    let foundSeries = false;

    const bookmark = {
      stockpileId: id.toUpperCase(),
      requestId: state.requestId,
      inputStockpile: state.stockpileName,
      sensorId: tag,
      text: flag.text,
      title: flag.title,
      x: x,
      userInfo,
    };

    for (let i = 0; i < newData.length; i++) {
      if (newData[i]?.id === `bookmarks_${spNum}`) {
        const foundFlags = newData[i].data.filter((item) => item.x === x);

        if (!foundFlags.length) {
          newData[i].data.push(flag);
        }

        foundSeries = true;
      }
    }

    if (!foundSeries) {
      const bookmarksSeries = {
        type: "flags",
        shape: "squarepin",
        name: `🔖 ${spNum}`,
        id: `bookmarks_${spNum}`,
        data: [flag],
        onSeries: id.toUpperCase(),
        width: 20,
      };
      newData.push(bookmarksSeries);
    }

    createBookmark({
      variables: bookmark,
    });

    trackEvent(APP_INSIGHTS_EVENTS.CREATE_BOOKMARK, { bookmark });

    setFlagComment("");
    setFlagTitle("");
    setGraphData(JSON.parse(JSON.stringify(newData)));
    setShowCreateFlagModal(false);
  };

  const handleDelete = () => {
    const { options, series }: any = graphClickEvent;
    const x = options.x;
    const id = series.userOptions.id.toLowerCase();
    let newData = [...graphData];
    let deletedSeriesIndex: number;
    let localBookmark: any;

    for (let i = 0; i < newData.length; i++) {
      if (newData[i]?.id === id) {
        for (let j = 0; j < newData[i].data.length; j++) {
          if (newData[i].data[j].x === x) {
            localBookmark = _.find(savedBookmarks, {
              x: newData[i].data[j].x,
              title: newData[i].data[j].title,
              text: newData[i].data[j].text,
            });
            newData[i].data.splice(j, 1);
            if (!newData[i].data.length) {
              deletedSeriesIndex = i;
            }
          }
        }
      }
    }

    if (deletedSeriesIndex !== undefined) {
      newData.splice(deletedSeriesIndex, 1);
    }

    const bookmarkToDelete =
      options.id && options.stockpileId
        ? {
            stockpileId: options.stockpileId,
            id: options.id,
            userInfo,
          }
        : localBookmark
        ? {
            stockpileId: localBookmark.stockpileId,
            id: localBookmark.id,
            userInfo,
          }
        : null;

    if (bookmarkToDelete) {
      deleteBookmark({ variables: bookmarkToDelete });
      trackEvent(APP_INSIGHTS_EVENTS.DELETE_BOOKMARK, {
        bookmark: bookmarkToDelete,
      });
    }

    setBookmarkForDelete("");
    setGraphData(JSON.parse(JSON.stringify(newData)));
    setShowDeleteFlagModal(false);
  };

  const handleClose = () => {
    setFlagComment("");
    setFlagTitle("");
    setBookmarkForDelete("");
    setShowCreateFlagModal(false);
    setShowDeleteFlagModal(false);
  };

  const handleCommentChange = (e: any) => {
    if (e.target.value.length < 256) {
      setFlagComment(e.target.value);
    }
  };

  const handleTitleChange = (e: any) => {
    if (e.target.value.length < 6) {
      setFlagTitle(e.target.value);
    }
  };

  const handlePointClick = (e: any) => {
    e.preventDefault();
    const id = e.point.series.userOptions.id.toLowerCase();
    const title = e.point?.options?.title || "";
    const seriesTag = id.replace(/[^a-z]/gi, "");

    const point = {
      options: e.point.options,
      series: e.point.series,
    };

    setGraphClickEvent(point);

    if (seriesTag === "sp") {
      setShowCreateFlagModal(true);
      trackEvent(APP_INSIGHTS_EVENTS.CLICK_SERIES_POINT, { graph: dataId });
      return;
    }

    if (seriesTag === "bookmarks") {
      setBookmarkForDelete(title);
      setShowDeleteFlagModal(true);
      trackEvent(APP_INSIGHTS_EVENTS.CLICK_BOOKMARK, { graph: dataId });
      return;
    }
  };

  return (
    <React.Fragment>
      {loading || bookmarksLoading ? (
        <IntraTableMessage>
          <LoaderBar />
        </IntraTableMessage>
      ) : error ? (
        <div className="no-data-block">
          <div className="no-data-text">
            <ErrorMessage error={error} />
          </div>
        </div>
      ) : chartOptions &&
        chartOptions.series[0] &&
        hasNumberPairs(chartOptions.series[0].data) &&
        hasStockpileData(chartOptions) ? (
        <React.Fragment>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            ref={chartRef}
          />
          <ModalWindow show={showCreateFlagModal}>
            <CreateFlagModal
              handleApply={handleSave}
              handleClose={handleClose}
              handleCommentChange={handleCommentChange}
              handleTitleChange={handleTitleChange}
              flagTitle={flagTitle}
              flagComment={flagComment}
            />
          </ModalWindow>
          <ModalWindow show={showDeleteFlagModal}>
            <DeleteFlagModal
              handleApply={handleDelete}
              handleClose={handleClose}
              bookmark={bookmarkForDelete}
            ></DeleteFlagModal>
          </ModalWindow>
        </React.Fragment>
      ) : !hasStockpileData(chartOptions) ? (
        <div className="no-data-block">
          <div className="no-data-text">
            Data is not available for selected stockpiles.
          </div>
        </div>
      ) : (
        <div>
          The application has encountered a data issue and is unable to render
          the graph.
        </div>
      )}
    </React.Fragment>
  );
};

export default GraphHighchart;
